import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Upload, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { createHawkFind } from "../services/ProductsService";
import { HAWK_FIND_RETAILER_OPTIONS } from "../config/hawkFindRetailers";

interface CreateHawkFindModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export default function CreateHawkFindModal({ isOpen, onClose, onCreated }: CreateHawkFindModalProps) {
  const [title, setTitle] = useState("");
  const [sku, setSku] = useState("");
  const [retailer, setRetailer] = useState("walmart");
  const [discountPrice, setDiscountPrice] = useState("");
  const [ogPrice, setOgPrice] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (file: File | undefined) => {
    if (!file) return;
    setPhoto(file);
    const url = URL.createObjectURL(file);
    setPreview(url);
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
  };

  const reset = () => {
    setTitle("");
    setSku("");
    setRetailer("walmart");
    setDiscountPrice("");
    setOgPrice("");
    clearPhoto();
  };

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleSubmit = async () => {
    if (!photo || !title.trim() || !sku.trim() || !discountPrice || !ogPrice) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      await createHawkFind({
        photo,
        title: title.trim(),
        sku: sku.trim(),
        retailer,
        discountPrice: parseFloat(discountPrice),
        ogPrice: parseFloat(ogPrice),
      });
      toast.success("Hawk Find created!");
      reset();
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputClass = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-purple-500/40 focus:border-purple-400 outline-none";
  const labelClass = "block text-xs font-semibold text-gray-700 dark:text-dark-200 mb-1";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      {/* Backdrop — desktop only */}
      <div className="hidden sm:block absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal shell */}
      <div className="relative h-full sm:h-auto sm:max-h-[90vh] sm:max-w-md sm:mx-auto sm:mt-[5vh] sm:rounded-xl bg-white dark:bg-dark-800 sm:shadow-xl flex flex-col min-h-0 overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 dark:border-dark-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">New Hawk Find</h3>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="p-1 text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-200 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain">
          <fieldset disabled={submitting} className="w-full px-5 py-4 space-y-4 disabled:opacity-80">
            {/* Photo upload */}
          <div>
            <label className={labelClass}>Photo</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              disabled={submitting}
              className="hidden"
              onChange={(e) => handleFileChange(e.target.files?.[0])}
            />
            {preview ? (
              <div className="relative w-2/3 sm:w-1/2 mx-auto aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 dark:bg-dark-700">
                <img
                  src={preview}
                  alt="Preview"
                  className="absolute inset-0 block w-full h-full object-cover"
                />
                <button
                  onClick={(e) => { e.stopPropagation(); clearPhoto(); }}
                  disabled={submitting}
                  className="absolute z-10 top-2 right-2 p-1.5 bg-black/60 text-white rounded-full hover:bg-black/80 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={14} />
                </button>
                <button
                  onClick={() => { if (!submitting) fileRef.current?.click(); }}
                  disabled={submitting}
                  className="absolute z-10 bottom-3 right-3 px-3 py-1.5 bg-black/60 text-white text-[11px] font-medium rounded-md hover:bg-black/80 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Change
                </button>
              </div>
            ) : (
              <button
                onClick={() => { if (!submitting) fileRef.current?.click(); }}
                disabled={submitting}
                className="w-2/3 sm:w-1/2 mx-auto aspect-[4/3] border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-dark-500 hover:border-purple-400 hover:text-purple-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload size={28} />
                <span className="text-xs font-medium">Upload photo</span>
              </button>
            )}
          </div>

          {/* Title */}
          <div>
            <label className={labelClass}>Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Ninja Air Fryer Clearance"
              className={inputClass}
            />
          </div>

          {/* SKU */}
          <div>
            <label className={labelClass}>SKU / UPC</label>
            <input
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="e.g. 123456789012"
              className={inputClass}
            />
          </div>

          {/* Retailer */}
          <div>
            <label className={labelClass}>Retailer</label>
            <select
              value={retailer}
              onChange={(e) => setRetailer(e.target.value)}
              className={inputClass}
            >
              {HAWK_FIND_RETAILER_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Prices */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Sale Price</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-dark-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={discountPrice}
                  onChange={(e) => setDiscountPrice(e.target.value)}
                  placeholder="49.00"
                  className={`${inputClass} pl-7`}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Original Price</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-dark-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={ogPrice}
                  onChange={(e) => setOgPrice(e.target.value)}
                  placeholder="129.00"
                  className={`${inputClass} pl-7`}
                />
              </div>
            </div>
          </div>
          </fieldset>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100 dark:border-dark-700 flex gap-3 pb-safe z-10">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="flex-1 py-2.5 rounded-lg bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200 text-sm font-medium hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors disabled:opacity-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !photo || !title.trim() || !sku.trim() || !discountPrice || !ogPrice}
            className="flex-1 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer inline-flex items-center justify-center gap-1.5"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            {submitting ? "Posting..." : "Post"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
